import { supabase } from '../lib/supabaseClient';

export type MarketplacePlatform = 'yemeksepeti' | 'trendyol' | 'getir' | 'migros';
export type ConfigurableMarketplacePlatform = Exclude<MarketplacePlatform, 'yemeksepeti'>;

const MARKETPLACE_PLATFORM_IDS: readonly MarketplacePlatform[] = [
  'yemeksepeti',
  'trendyol',
  'getir',
  'migros',
];

export const MARKETPLACE_PLATFORM_LABELS: Readonly<Record<MarketplacePlatform, string>> = {
  yemeksepeti: 'Yemeksepeti',
  trendyol: 'Trendyol Go',
  getir: 'Getir',
  migros: 'Migros Yemek',
};

export const MARKETPLACE_PLATFORMS: ReadonlyArray<{
  id: ConfigurableMarketplacePlatform;
  label: string;
}> = [
  { id: 'trendyol', label: MARKETPLACE_PLATFORM_LABELS.trendyol },
  { id: 'getir', label: MARKETPLACE_PLATFORM_LABELS.getir },
  { id: 'migros', label: MARKETPLACE_PLATFORM_LABELS.migros },
];

export interface MarketplaceExtensionAccount {
  platformAccountId: string | null;
  platform: MarketplacePlatform;
  externalRestaurantId: string | null;
  displayName: string | null;
  boundAt: string | null;
  lastSeenAt: string | null;
}

export interface MarketplaceExtensionSyncCounts {
  received: number | null;
  inserted: number | null;
  updated: number | null;
  unchanged: number | null;
  rejected: number | null;
}

export interface MarketplaceExtensionSyncSummary {
  status: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  counts: MarketplaceExtensionSyncCounts;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface MarketplaceExtensionInstallation {
  id: string;
  restaurantId: string;
  label: string | null;
  allowedPlatforms: MarketplacePlatform[];
  externalRestaurantIds: Partial<Record<MarketplacePlatform, string>>;
  extensionId: string | null;
  extensionVersion: string | null;
  deviceLabel: string | null;
  createdAt: string;
  expiresAt: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
  state: string | null;
  accounts: MarketplaceExtensionAccount[];
  latestSync: MarketplaceExtensionSyncSummary | null;
}

export interface IssuePairingCodeInput {
  restaurantId: string;
  allowedPlatforms: ConfigurableMarketplacePlatform[];
  externalRestaurantIds: Partial<Record<ConfigurableMarketplacePlatform, string>>;
  label?: string;
}

export interface PairingCodeResult {
  pairingCode: string;
  expiresAt: string;
  restaurantId: string;
  allowedPlatforms: ConfigurableMarketplacePlatform[];
  externalRestaurantIds: Partial<Record<ConfigurableMarketplacePlatform, string>>;
  label: string | null;
}

interface AdminFunctionResponse<T> {
  ok: boolean;
  data?: T;
  error?: unknown;
  requestId?: string;
}

type AdminActionBody =
  | ({ action: 'issue_pairing_code' } & IssuePairingCodeInput)
  | { action: 'list_installations'; restaurantId: string }
  | { action: 'revoke_installation'; installationId: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readValue = (record: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return undefined;
};

const readString = (record: Record<string, unknown>, ...keys: string[]): string | null => {
  const value = readValue(record, ...keys);
  return typeof value === 'string' && value.trim() ? value : null;
};

const readNumber = (record: Record<string, unknown>, ...keys: string[]): number | null => {
  const value = readValue(record, ...keys);
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const isMarketplacePlatform = (value: unknown): value is MarketplacePlatform =>
  value === 'yemeksepeti' ||
  value === 'trendyol' ||
  value === 'getir' ||
  value === 'migros';

const readPlatforms = (value: unknown): MarketplacePlatform[] =>
  Array.isArray(value) ? Array.from(new Set(value.filter(isMarketplacePlatform))) : [];

const readExternalRestaurantIds = (
  value: unknown,
): Partial<Record<MarketplacePlatform, string>> => {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    MARKETPLACE_PLATFORM_IDS.flatMap((id) => {
      const externalId = readString(value, id);
      return externalId ? [[id, externalId]] : [];
    }),
  );
};

const mapAccount = (value: unknown): MarketplaceExtensionAccount | null => {
  if (!isRecord(value)) return null;
  const platform = readValue(value, 'platform');
  if (!isMarketplacePlatform(platform)) return null;

  return {
    platform,
    platformAccountId: readString(
      value,
      'platformAccountId',
      'platform_account_id',
      'accountId',
      'account_id',
    ),
    externalRestaurantId: readString(
      value,
      'externalRestaurantId',
      'external_restaurant_id',
    ),
    displayName: readString(value, 'displayName', 'display_name', 'label'),
    boundAt: readString(value, 'boundAt', 'bound_at'),
    lastSeenAt: readString(value, 'lastSeenAt', 'last_seen_at'),
  };
};

const mapLatestSync = (value: unknown): MarketplaceExtensionSyncSummary | null => {
  if (!isRecord(value)) return null;
  const countsValue = readValue(value, 'counts');
  const counts = isRecord(countsValue) ? countsValue : {};
  return {
    status: readString(value, 'status'),
    startedAt: readString(value, 'startedAt', 'started_at'),
    finishedAt: readString(value, 'finishedAt', 'finished_at'),
    counts: {
      received: readNumber(counts, 'received'),
      inserted: readNumber(counts, 'inserted'),
      updated: readNumber(counts, 'updated'),
      unchanged: readNumber(counts, 'unchanged'),
      rejected: readNumber(counts, 'rejected'),
    },
    errorCode: readString(value, 'errorCode', 'error_code'),
    errorMessage: readString(value, 'errorMessage', 'error_message'),
  };
};

const mapInstallation = (value: unknown): MarketplaceExtensionInstallation | null => {
  if (!isRecord(value)) return null;
  const id = readString(value, 'id', 'installationId', 'installation_id');
  const restaurantId = readString(value, 'restaurantId', 'restaurant_id');
  if (!id || !restaurantId) return null;

  const rawAccounts = readValue(value, 'accounts');
  const accounts = Array.isArray(rawAccounts)
    ? rawAccounts.map(mapAccount).filter((account): account is MarketplaceExtensionAccount => Boolean(account))
    : [];
  const declaredPlatforms = readPlatforms(
    readValue(value, 'allowedPlatforms', 'allowed_platforms'),
  );

  return {
    id,
    restaurantId,
    label: readString(value, 'label'),
    allowedPlatforms:
      declaredPlatforms.length > 0
        ? declaredPlatforms
        : Array.from(new Set(accounts.map((account) => account.platform))),
    externalRestaurantIds: readExternalRestaurantIds(
      readValue(value, 'externalRestaurantIds', 'external_restaurant_ids'),
    ),
    extensionId: readString(value, 'extensionId', 'extension_id'),
    extensionVersion: readString(value, 'extensionVersion', 'extension_version'),
    deviceLabel: readString(value, 'deviceLabel', 'device_label'),
    createdAt: readString(value, 'createdAt', 'created_at') ?? '',
    expiresAt: readString(
      value,
      'expiresAt',
      'expires_at',
      'tokenExpiresAt',
      'token_expires_at',
    ),
    lastSeenAt: readString(value, 'lastSeenAt', 'last_seen_at'),
    revokedAt: readString(value, 'revokedAt', 'revoked_at'),
    state: readString(value, 'state'),
    accounts,
    latestSync: mapLatestSync(readValue(value, 'latestSync', 'latest_sync')),
  };
};

const asErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value;
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message.trim()) return record.message;
  if (typeof record.error === 'string' && record.error.trim()) return record.error;
  return null;
};

const callMarketplaceExtensionAdmin = async <T>(body: AdminActionBody): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('marketplace-extension-admin', {
    body,
  });

  if (error) {
    throw new Error(error.message || 'Pazar yeri eklenti servisine ulaşılamadı.');
  }

  const response = data as AdminFunctionResponse<T> | null;
  if (!response?.ok || response.data === undefined) {
    const message = asErrorMessage(response?.error);
    const requestSuffix = response?.requestId ? ` (İstek: ${response.requestId})` : '';
    throw new Error(`${message ?? 'İşlem tamamlanamadı.'}${requestSuffix}`);
  }

  return response.data;
};

export async function issueMarketplaceExtensionPairingCode(
  input: IssuePairingCodeInput,
): Promise<PairingCodeResult> {
  return callMarketplaceExtensionAdmin<PairingCodeResult>({
    action: 'issue_pairing_code',
    restaurantId: input.restaurantId,
    allowedPlatforms: input.allowedPlatforms,
    externalRestaurantIds: input.externalRestaurantIds,
    ...(input.label?.trim() ? { label: input.label.trim() } : {}),
  });
}

export async function listMarketplaceExtensionInstallations(
  restaurantId: string,
): Promise<MarketplaceExtensionInstallation[]> {
  const result = await callMarketplaceExtensionAdmin<{
    installations: unknown[];
  }>({
    action: 'list_installations',
    restaurantId,
  });

  return (result.installations ?? [])
    .map(mapInstallation)
    .filter(
      (installation): installation is MarketplaceExtensionInstallation => Boolean(installation),
    );
}

export async function revokeMarketplaceExtensionInstallation(
  installationId: string,
): Promise<{ installationId: string; revokedAt: string }> {
  return callMarketplaceExtensionAdmin<{ installationId: string; revokedAt: string }>({
    action: 'revoke_installation',
    installationId,
  });
}
